import { useState, useEffect, useCallback } from 'react'
import { Box, Flex, Text, VStack, Grid, Modal, ConfirmDialog, useT } from '@minetrace/ui'
import {
  useFormColors,
  TrackedInput,
  TrackedSelect,
  TrackedDatePicker,
  FileInput,
  withFormProvider,
} from '@minetrace/ui/forms'
import { useStep4IgafomForm, type Step4IgafomFormValues } from './useStep4IgafomForm'
import {
  LuChevronDown as ChevronDown,
  LuChevronRight as ChevronRight,
  LuFileText as FileText,
  LuPlus as Plus,
  LuPencil as Pencil,
  LuTrash2 as Trash2,
} from 'react-icons/lu'
import type { MiningRightResponse, IgafomRecordResponse } from '../../../api/models/response/proveedor'
import MiningRightDataWarning from '../../../components/MiningRightDataWarning'
import type { AddIgafomRecordRequest } from '../../../api/models/request/proveedor'
import type { UseFormReturn } from 'react-hook-form'
import {
  MINERAL_SUBSTANCES, IGAFOM_DOCUMENT_STATUSES, IGAFOM_TYPES,
  EXPLOITATION_METHODS, PROCESSING_METHODS, ALL_MINING_METHODS, enumLabel, type EnumOption,
} from '../../../data/enums'
import { getMiningActivityType } from '../../../utils/miningRightActivity'
import DocumentoDescarga from '../../../components/DocumentoDescarga'
import { useDeleteWarning } from '../../../lib/modificationMode'

const DOCUMENT_STATUS_TIDS: Record<number, string> = {
  1: 'proveedores.wizard.step4.statusEnEvaluacion',
  2: 'proveedores.wizard.step4.statusAprobado',
  3: 'proveedores.wizard.step4.statusObservado',
  4: 'proveedores.wizard.step4.statusRechazado',
}
const DOCUMENT_STATUSES = IGAFOM_DOCUMENT_STATUSES.map(opt => ({
  ...opt,
  labelTid: DOCUMENT_STATUS_TIDS[opt.value],
}))

const SUBSTANCE_OPTIONS = MINERAL_SUBSTANCES.filter((opt) => opt.value !== 0)

const IGAFOM_TYPE_OPTIONS = IGAFOM_TYPES.map((opt) => ({ ...opt }))

// ── Modal Form (inner) ──

interface IgafomFormInnerProps {
  formMethods: UseFormReturn<Step4IgafomFormValues>
  isEditing: boolean
  isBusy: boolean
  /** Opciones del selector de método: explotación o procesamiento según la actividad del derecho. */
  methodOptions: EnumOption[]
  methodLabelTid: string
  /** True cuando minerSource === 'Reinfo': fecha caducidad deshabilitada con default 31/12/2026 (Ley 32537). */
  isReinfo: boolean
  /** True cuando minerSource === 'FormalizedMiner': fecha caducidad requerida y máx 5 años desde la emisión. */
  isFormalized: boolean
  onSave: (values: Step4IgafomFormValues, receiptFile?: File, documentFile?: File) => void
  onCancel: () => void
  onRegisterSubmit?: (fn: () => void) => void
}

const IgafomFormInner = withFormProvider<Step4IgafomFormValues, IgafomFormInnerProps>(
  ({ formMethods, isEditing, isBusy, methodOptions, methodLabelTid, isReinfo, isFormalized, onSave, onRegisterSubmit }) => {
    const { t } = useT()
    const c = useFormColors()
    const [receiptFile, setReceiptFile] = useState<File | null>(null)
    const [documentFile, setDocumentFile] = useState<File | null>(null)
    const [fileErrors, setFileErrors] = useState<{ receipt?: string; document?: string }>({})

    const handleFormSubmit = useCallback(async () => {
      const isValid = await formMethods.trigger()
      const errors: { receipt?: string; document?: string } = {}
      if (!isEditing && !receiptFile) errors.receipt = t('proveedores.wizard.step4.receiptRequired')
      if (!isEditing && !documentFile) errors.document = t('proveedores.wizard.step4.documentRequired')
      setFileErrors(errors)

      // Obs 9: validación condicional de fecha de caducidad para minero formalizado.
      if (isFormalized) {
        const values = formMethods.getValues()
        if (!values.expirationDate) {
          formMethods.setError('expirationDate', { message: t('proveedores.wizard.step4.expirationFormalizedRequired') })
          return
        }
        if (values.presentationDate && values.expirationDate) {
          const pres = new Date(values.presentationDate)
          const exp = new Date(values.expirationDate)
          const maxExp = new Date(pres)
          maxExp.setFullYear(maxExp.getFullYear() + 5)
          if (exp > maxExp) {
            formMethods.setError('expirationDate', { message: t('proveedores.wizard.step4.expirationFormalizedMaxYears') })
            return
          }
        }
      }

      if (!isValid || Object.keys(errors).length > 0) return
      onSave(formMethods.getValues(), receiptFile ?? undefined, documentFile ?? undefined)
    }, [formMethods, isEditing, receiptFile, documentFile, onSave, t, isFormalized])

    // Register submit function with parent for modal footer
    useEffect(() => {
      onRegisterSubmit?.(() => handleFormSubmit())
    }, [onRegisterSubmit, handleFormSubmit])

    const today = new Date().toISOString().split('T')[0]

    return (
      <Box opacity={isBusy ? 0.5 : 1} pointerEvents={isBusy ? 'none' : 'auto'} transition="opacity 0.2s">
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
          <TrackedDatePicker name="presentationDate" labelTid="proveedores.wizard.step4.presentationDateLabel" maxDate={today} />
          <Box>
            <TrackedDatePicker name="expirationDate" labelTid="proveedores.wizard.step4.expirationDateLabel" disabled={isReinfo} />
            {isReinfo && (
              <Text tid="proveedores.wizard.step4.expirationReinfoHint" fontSize="xs" color={c.textMuted} mt={1} display="block" />
            )}
          </Box>
          <TrackedSelect name="type" labelTid="proveedores.wizard.step4.typeLabel" options={IGAFOM_TYPE_OPTIONS} placeholderTid="proveedores.wizard.step4.selectPlaceholder" />
          <TrackedInput name="fileNumber" labelTid="proveedores.wizard.step4.fileNumberLabel" placeholderTid="proveedores.wizard.step4.fileNumberPlaceholder" maxLength={100} />
          <TrackedSelect name="documentStatus" labelTid="proveedores.wizard.step4.documentStatusLabel" options={DOCUMENT_STATUSES} placeholderTid="proveedores.wizard.step4.selectPlaceholder" />
          <TrackedSelect name="exploitationMethod" labelTid={methodLabelTid} options={methodOptions} placeholderTid="proveedores.wizard.step4.selectPlaceholder" />
          <TrackedSelect name="substanceId" labelTid="proveedores.wizard.step4.substanceLabel" options={SUBSTANCE_OPTIONS} placeholderTid="proveedores.wizard.step4.selectPlaceholder" />
          <TrackedInput name="productiveCapacity" type="number" labelTid="proveedores.wizard.step4.productiveCapacityLabel" placeholderTid="proveedores.wizard.step4.productiveCapacityPlaceholder" />
        </Grid>

        <Grid templateColumns="1fr" gap={4} mt={4}>
          <FileInput
            labelTid="proveedores.wizard.step4.receiptLabel"
            required
            accept=".pdf,.jpg"
            maxSizeMb={5}
            value={receiptFile}
            onChange={(f) => { setReceiptFile(f); setFileErrors(prev => ({ ...prev, receipt: undefined })) }}
            error={fileErrors.receipt}
          />
          {/* 30 MB es el techo real, no una elección: Cloud Run rechaza los requests
              HTTP/1 por encima de 32 MiB antes de que lleguen a la aplicación. Superarlo
              exige subir directo al bucket con URL firmada. */}
          <FileInput
            labelTid="proveedores.wizard.step4.documentLabel"
            required
            accept=".pdf"
            maxSizeMb={30}
            value={documentFile}
            onChange={(f) => { setDocumentFile(f); setFileErrors(prev => ({ ...prev, document: undefined })) }}
            error={fileErrors.document}
          />
        </Grid>

      </Box>
    )
  },
  useStep4IgafomForm,
)

// ── Main Component ──

interface Step4IgafomProps {
  mode: 'edit' | 'readonly'
  companyId: string | null
  savedMiningRights: MiningRightResponse[]
  /** Fuente del minero: define si el documento exigido es IGAFOM (en formalización) o IGAC (formalizado). */
  minerSource?: string | null
  onAdd: (mrId: string, data: AddIgafomRecordRequest, receiptFile?: File, documentFile?: File) => Promise<void>
  onUpdate: (mrId: string, igId: string, data: AddIgafomRecordRequest, receiptFile?: File, documentFile?: File) => Promise<void>
  onRemove: (mrId: string, igId: string) => Promise<void>
}

export default function Step4Igafom({ mode, savedMiningRights, minerSource, onAdd, onUpdate, onRemove }: Step4IgafomProps) {
  const c = useFormColors()
  const { t } = useT()
  const isReadonly = mode === 'readonly'

  // Un minero formalizado presenta IGAC; uno en vía de formalización, IGAFOM.
  const docType =
    minerSource === 'FormalizedMiner' ? 'IGAC'
    : minerSource === 'Reinfo' ? 'IGAFOM'
    : 'IGAFOM/IGAC'

  const [expandedRights, setExpandedRights] = useState<Set<string>>(new Set())
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMrId, setModalMrId] = useState<string | null>(null)
  const [editingRecord, setEditingRecord] = useState<IgafomRecordResponse | null>(null)
  const [isBusy, setIsBusy] = useState(false)
  const [submitFn, setSubmitFn] = useState<(() => void) | null>(null)
  const deleteWarning = useDeleteWarning()
  const [deleteTarget, setDeleteTarget] = useState<{ mrId: string; igId: string; name: string } | null>(null)

  // El derecho del modal define si se piden métodos de explotación o de procesamiento (observación 10).
  const modalRight = modalMrId ? savedMiningRights.find(r => r.id === modalMrId) : null
  const modalIsBeneficio = getMiningActivityType(modalRight?.name) === 'beneficio'
  const modalMethodOptions = modalIsBeneficio ? PROCESSING_METHODS : EXPLOITATION_METHODS
  const modalMethodLabelTid = modalIsBeneficio
    ? 'proveedores.wizard.step4.processingMethodLabel'
    : 'proveedores.wizard.step4.exploitationMethodLabel'

  // Obs 9: fecha de caducidad condicional al minerSource.
  const modalIsReinfo = minerSource === 'Reinfo'
  const modalIsFormalized = minerSource === 'FormalizedMiner'
  // Reinfo: default 31/12/2026 (Ley 32537) cuando no hay valor previo. Formalizado y otros: sin default.
  const modalDefaultExpirationDate = editingRecord?.expirationDate
    ?? (modalIsReinfo ? '2026-12-31' : '')

  const toggleRight = (id: string) => {
    setExpandedRights(prev => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) } else { next.add(id) }
      return next
    })
  }

  const openAddModal = (mrId: string) => {
    setModalMrId(mrId)
    setEditingRecord(null)
    setModalOpen(true)
  }

  const openEditModal = (mrId: string, rec: IgafomRecordResponse) => {
    setModalMrId(mrId)
    setEditingRecord(rec)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setModalMrId(null)
    setEditingRecord(null)
  }

  const handleSave = async (values: Step4IgafomFormValues, receiptFile?: File, documentFile?: File) => {
    if (!modalMrId) return
    const data: AddIgafomRecordRequest = {
      presentationDate: values.presentationDate || undefined,
      expirationDate: values.expirationDate || undefined,
      fileNumber: values.fileNumber || undefined,
      type: values.type || undefined,
      exploitationMethod: values.exploitationMethod || undefined,
      documentStatus: values.documentStatus || undefined,
      substanceId: values.substanceId || undefined,
      productiveCapacity: values.productiveCapacity ? String(values.productiveCapacity) : undefined,
    }
    setIsBusy(true)
    try {
      if (editingRecord) {
        await onUpdate(modalMrId, editingRecord.id, data, receiptFile, documentFile)
      } else {
        await onAdd(modalMrId, data, receiptFile, documentFile)
      }
      closeModal()
    } finally { setIsBusy(false) }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    setIsBusy(true)
    try {
      await onRemove(deleteTarget.mrId, deleteTarget.igId)
      setDeleteTarget(null)
    } finally { setIsBusy(false) }
  }

  return (
    <VStack gap={6} alignItems="stretch">
      {savedMiningRights.length === 0 ? (
        <Box p={8} textAlign="center" borderRadius="xl" border="1px dashed" borderColor="gray.200">
          <FileText size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <Text tid="proveedores.wizard.step4.emptyMessage" fontSize="sm" color={c.textMuted} />
        </Box>
      ) : (
        <VStack gap={4} alignItems="stretch">
          {savedMiningRights.map(right => {
            const isExpanded = expandedRights.has(right.id)
            const records = right.igafomRecords ?? []

            return (
              <Box key={right.id} borderRadius="xl" border="1px solid" borderColor={c.border} overflow="hidden">
                {/* Header */}
                <Flex px={5} py={3} bg={c.bgHover} alignItems="center" justifyContent="space-between" cursor="pointer" onClick={() => toggleRight(right.id)}>
                  <Flex alignItems="center" gap={2}>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <Text fontSize="sm" fontWeight="600" color={c.text}>{t('common.miningRightLabel')}: {right.name || right.uniqueCode}</Text>
                    <Text fontSize="xs" color={c.textMuted}>({right.uniqueCode})</Text>
                  </Flex>
                  <Flex alignItems="center" gap={3}>
                    <MiningRightDataWarning hasBoundary={right.hasBoundary} componentCount={right.components?.length ?? 0} />
                    <Text tid="proveedores.wizard.step4.recordsCount" values={{ count: records.length }} fontSize="xs" color={c.textMuted} />
                  </Flex>
                </Flex>

                {/* Expanded */}
                {isExpanded && (
                  <Box px={5} py={4}>
                    {/* Add button */}
                    {!isReadonly && (
                      <Flex mb={4}>
                        <Box
                          as="button" type="button" display="flex" alignItems="center" gap={2}
                          px={4} py={2} bg={c.accent} color="white" borderRadius="md" fontSize="sm" fontWeight="600"
                          cursor="pointer" _hover={{ opacity: 0.9 }} onClick={() => openAddModal(right.id)}
                        >
                          <Plus size={14} />{t('proveedores.wizard.step4.addButton', { docType })}
                        </Box>
                      </Flex>
                    )}

                    {/* Records list */}
                    {records.length > 0 ? (
                      <VStack gap={3} alignItems="stretch">
                        {records.map(rec => (
                          <Box key={rec.id} p={4} bg={c.bgInput} borderRadius="lg" border="1px solid" borderColor={c.border}>
                            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={3}>
                              <Box>
                                <Text tid="proveedores.wizard.step4.emissionDate" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{rec.presentationDate ?? '—'}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4.expirationDate" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{rec.expirationDate ?? '—'}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4.fileNumber" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{rec.fileNumber ?? '—'}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4.type" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{rec.typeName ?? '—'}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4.exploitationMethod" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{enumLabel(ALL_MINING_METHODS, rec.exploitationMethod)}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4.documentStatus" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{rec.documentStatusName ?? '—'}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4.substance" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{rec.substanceName ?? '—'}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4.productiveCapacity" fontSize="xs" color={c.textMuted} />
                                <Text fontSize="sm" color={c.text}>{rec.productiveCapacity ?? '—'}</Text>
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4.receipt" fontSize="xs" color={c.textMuted} />
                                {rec.receiptFile ? (
                                  <DocumentoDescarga caption="Cargo de Presentación" storagePath={rec.receiptFile.storagePath} fileName={rec.receiptFile.originalFileName} />
                                ) : <Text tid="proveedores.wizard.step4.noFile" fontSize="sm" color={c.textMuted} />}
                              </Box>
                              <Box>
                                <Text tid="proveedores.wizard.step4.document" fontSize="xs" color={c.textMuted} />
                                {rec.documentFile ? (
                                  <DocumentoDescarga caption="Documento IGAFOM/IGAC" storagePath={rec.documentFile.storagePath} fileName={rec.documentFile.originalFileName} />
                                ) : <Text tid="proveedores.wizard.step4.noFile" fontSize="sm" color={c.textMuted} />}
                              </Box>
                            </Grid>

                            {!isReadonly && (
                              <Flex mt={3} gap={2} justifyContent="flex-end">
                                <Box as="button" type="button" display="flex" alignItems="center" gap={1} px={3} py={1.5} bg={`${c.accent}15`} color={c.accent} borderRadius="lg" fontSize="xs" fontWeight="600" cursor="pointer" _hover={{ bg: `${c.accent}25` }} onClick={() => openEditModal(right.id, rec)}>
                                  <Pencil size={12} />{t('proveedores.wizard.step4.edit')}
                                </Box>
                                <Box as="button" type="button" display="flex" alignItems="center" gap={1} px={3} py={1.5} bg="red.50" color="red.600" borderRadius="lg" fontSize="xs" fontWeight="600" cursor="pointer" _hover={{ bg: 'red.100' }} onClick={() => setDeleteTarget({ mrId: right.id, igId: rec.id, name: rec.fileNumber ?? t('proveedores.wizard.step4.defaultRecordName') })}>
                                  <Trash2 size={12} />{t('proveedores.wizard.step4.delete')}
                                </Box>
                              </Flex>
                            )}
                          </Box>
                        ))}
                      </VStack>
                    ) : (
                      <Text tid="proveedores.wizard.step4.noRecordsForRight" values={{ docType }} fontSize="sm" color={c.textMuted} textAlign="center" py={4} />
                    )}
                  </Box>
                )}
              </Box>
            )
          })}
        </VStack>
      )}

      {/* Modal for add/edit */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingRecord ? t('proveedores.wizard.step4.modalTitleEdit', { docType }) : t('proveedores.wizard.step4.modalTitleNew', { docType })}
        subtitle={modalMrId ? savedMiningRights.find(r => r.id === modalMrId)?.name : undefined}
        size="lg"
        footer={
          <>
            <Box as="button" type="button" px={5} py={2.5} borderRadius="md" fontSize="sm" fontWeight="600"
              color={c.textMuted} border="1px solid" borderColor={c.border}
              cursor="pointer" _hover={{ bg: c.bgHover }} onClick={closeModal}
            >
              {t('proveedores.wizard.step4.cancel')}
            </Box>
            <Box
              as="button" type="button" display="flex" alignItems="center" gap={2}
              px={6} py={2.5} bg={c.accent} color="white" borderRadius="md" fontSize="sm" fontWeight="600"
              cursor={isBusy ? 'not-allowed' : 'pointer'} opacity={isBusy ? 0.6 : 1}
              _hover={{ opacity: 0.9 }} onClick={() => submitFn?.()} disabled={isBusy}
            >
              {isBusy ? t('proveedores.wizard.step4.saving') : editingRecord ? t('proveedores.wizard.step4.update') : <><Plus size={14} /> {t('proveedores.wizard.step4.register')}</>}
            </Box>
          </>
        }
      >
        <IgafomFormInner
          key={editingRecord?.id ?? 'new'}
          defaultValues={{
            presentationDate: editingRecord?.presentationDate ?? '',
            expirationDate: modalDefaultExpirationDate,
            fileNumber: editingRecord?.fileNumber ?? '',
            type: editingRecord?.type ?? 0,
            documentStatus: editingRecord?.documentStatus ?? 0,
            exploitationMethod: editingRecord?.exploitationMethod ?? 0,
            substanceId: editingRecord?.substanceId ?? 0,
            productiveCapacity: editingRecord?.productiveCapacity ? Number(editingRecord.productiveCapacity) : 0,
          }}
          isEditing={Boolean(editingRecord)}
          isBusy={isBusy}
          methodOptions={modalMethodOptions}
          methodLabelTid={modalMethodLabelTid}
          isReinfo={modalIsReinfo}
          isFormalized={modalIsFormalized}
          onSave={handleSave}
          onCancel={closeModal}
          onRegisterSubmit={(fn) => setSubmitFn(() => fn)}
        />
      </Modal>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={t('proveedores.wizard.step4.deleteTitle', { docType })}
        confirmLabel={t('proveedores.wizard.step4.deleteConfirm')}
        variant="danger"
        isLoading={isBusy}
      >
        ¿Está seguro que desea eliminar el registro <Text as="span" fontWeight="600">{deleteTarget?.name}</Text>? {deleteWarning}
      </ConfirmDialog>
    </VStack>
  )
}
